import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { AuthorshipSchema, MessageSchema } from './primitives.js';
const c = initContract();
/**
 * Unit Groups (MD-12 field 7, OQ-05-18) — the OPTIONAL FOLDER units file
 * under. "A group is a folder, nothing more": it affects nothing but where a
 * unit appears in the tree (BR-MD-12-06, rewritten by R6), and OQ-05-18's
 * settled answer keeps it exactly because "a tenant with forty units wants
 * them sorted; a tenant with five can leave the field empty."
 *
 * THE DELETE IS THE ODD ONE. MD-12 §15 rules it in words — deleting the group
 * a unit sits in leaves the unit "working and shows as ungrouped" — so
 * deletion is allowed while units are filed (SET NULL at the database), a
 * deliberate departure from MD-RULE-3 for the one master that decides
 * nothing. The §4.20 confirm still runs.
 *
 * Permission area: `inventory`, exactly as units themselves.
 */
// ── The fixed sentences ─────────────────────────────────────────────────────
export const UNIT_GROUP_MESSAGES = {
    nameRequired: 'Type a name for this group.',
    nameTaken: (name) => `There is already a unit group called ${name}.`,
    inactive: (name) => `${name} is switched off and cannot be picked.`,
};
/**
 * D46 — the unit-group family's CONVERTED parameterised refusal, structured.
 * `nameTaken` composes its `UNIT_GROUP_MESSAGES` twin plus an ICU key and the
 * name param; sentence parity pinned by `refusal-census.spec.ts`.
 */
export const UNIT_GROUP_REFUSALS = {
    nameTaken: (name) => ({
        message: UNIT_GROUP_MESSAGES.nameTaken(name),
        key: 'unitGroups.errNameTaken',
        params: { name },
    }),
};
// ── Shapes ──────────────────────────────────────────────────────────────────
export const UnitGroupSchema = z
    .object({
    id: z.uuid(),
    /** UG-## — presentation of groupNo (MD-12 field 1); never typed. */
    code: z.string(),
    groupNo: z.number().int().positive(),
    name: z.string(),
    /** S5 / UOM-02: optional Arabic name; the Arabic UI shows `nameAr ?? name`. */
    nameAr: z.string().nullable(),
    isActive: z.boolean(),
    /** How many units are filed here — information, NOT a delete blocker. */
    unitsIn: z.number().int(),
    // MD-RULE-5's six fields, `createdAt` among them. rule-coverage:ignore
    ...AuthorshipSchema.shape,
})
    .strict();
export const CreateUnitGroupRequestSchema = z
    .object({
    name: z.string().trim().min(1, UNIT_GROUP_MESSAGES.nameRequired).max(60),
    nameAr: z.string().trim().min(1).max(60).optional(),
})
    .strict();
export const UpdateUnitGroupRequestSchema = z
    .object({
    name: z.string().trim().min(1, UNIT_GROUP_MESSAGES.nameRequired).max(60).optional(),
    /** null clears the Arabic name (the Arabic UI falls back to `name`). */
    nameAr: z.string().trim().min(1).max(60).nullable().optional(),
})
    .strict();
const groupParams = z.object({ groupId: z.uuid() }).strict();
export const unitGroupsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/unit-groups',
        query: z
            .object({
            q: z.string().optional(),
            includeInactive: z.enum(['true', 'false']).optional(),
        })
            .strict(),
        responses: {
            200: z.object({ groups: UnitGroupSchema.array() }).strict(),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'The folder list (inventory.view) — the left panel of MD-12 §4’s master–detail tree.',
    },
    create: {
        method: 'POST',
        path: '/api/unit-groups',
        body: CreateUnitGroupRequestSchema,
        responses: {
            201: UnitGroupSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            409: MessageSchema,
        },
        summary: 'New folder (inventory.create) — UG-## off the tenant’s own counter (MD-12 field 1).',
    },
    update: {
        method: 'PATCH',
        path: '/api/unit-groups/:groupId',
        pathParams: groupParams,
        body: UpdateUnitGroupRequestSchema,
        responses: {
            200: UnitGroupSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Rename (inventory.edit) — "the tenant creates, renames and switches off groups as freely as units" (MD-12 §5).',
    },
    deactivate: {
        method: 'POST',
        path: '/api/unit-groups/:groupId/deactivate',
        pathParams: groupParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: UnitGroupSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch off (inventory.edit) — not offered when filing; filed units stay filed.',
    },
    reactivate: {
        method: 'POST',
        path: '/api/unit-groups/:groupId/reactivate',
        pathParams: groupParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: UnitGroupSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Switch back on (inventory.edit).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/unit-groups/:groupId',
        pathParams: groupParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'Delete (inventory.delete) — allowed even with units filed: they keep working and show as ungrouped (MD-12 §15, SET NULL). The §4.20 confirm is the UI’s.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=unit-groups.js.map